import {
  ArrowBigUp,
  Camera,
  Eye,
  Flame,
  Forward,
  Heart,
  Hourglass,
  Image as ImageIcon,
  Images,
  MessageCircle,
  Repeat2,
  Send,
  ThumbsUp,
  TriangleAlert,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router'
import type { FeedItem } from '../../api/types'
import type { OverlayState } from '../../lib/feedNav'
import { formatAge, formatCount, formatPct, handleLabel } from '../../lib/format'
import { XBadge } from '../XBadge'
import { postHref } from '../../lib/measurement'
import { MeasurementNote } from '../MeasurementNote'

interface CardProps {
  item: FeedItem
  isNew: boolean
  now: number
}

const PLATFORM_ICON = { instagram: Camera, telegram: Send, reddit: ArrowBigUp } as const

export function TextCard({ item, isNew, now }: CardProps) {
  const location = useLocation()
  const state: OverlayState = { backgroundLocation: location }
  const Icon = PLATFORM_ICON[item.platform]

  return (
    <Link
      to={postHref(item)}
      state={state}
      aria-label={cardLabel(item)}
      className="group flex min-w-0 flex-col gap-2 rounded-xl bg-card p-3.5 ring-1 ring-line outline-offset-2 transition-[box-shadow] ring-inset hover:ring-fg/25"
    >
      <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted">
        <Icon size={13} className="shrink-0" aria-hidden />
        <span className="min-w-0 truncate font-medium text-fg">{handleLabel(item.platform, item.author.handle)}</span>
        <span className="shrink-0">· {formatAge(item.published_at, now)}</span>
        <span className="ml-auto flex shrink-0 items-center gap-1">
          {isNew && <span className="rounded-md bg-fg px-1.5 py-0.5 text-[11px] leading-none font-medium text-bg">новое</span>}
          <Marks item={item} />
          <Badge item={item} />
        </span>
      </div>

      {item.repost && (
        <div className="-mt-0.5 flex min-w-0 items-center gap-1 text-xs text-muted">
          <Repeat2 size={12} className="shrink-0" aria-hidden />
          <span className="truncate">
            репост из {item.repost.origin_handle ? `@${item.repost.origin_handle}` : 'закрытого канала'}
          </span>
        </div>
      )}

      {item.platform === 'reddit' ? (
        <RedditBody text={item.caption_snippet} />
      ) : item.platform === 'instagram' ? (
        <InstagramBody item={item} />
      ) : (
        <p className="line-clamp-6 text-[14px] leading-snug text-pretty whitespace-pre-line">
          {item.caption_snippet ?? 'Без текста'}
        </p>
      )}

      <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-0.5 text-[12px] text-muted">
        <Stats item={item} />
        {item.siblings > 0 && (
          <span className="ml-auto" title="Склеено с кросспостами или репостами того же поста">
            +{item.siblings} у других
          </span>
        )}
      </div>
      {item.platform === 'instagram' && <MeasurementNote value={item} compact />}
    </Link>
  )
}

function cardLabel(item: FeedItem): string {
  const who = handleLabel(item.platform, item.author.handle)
  const x = item.xn !== null ? `, ×${item.xn.toFixed(1).replace('.', ',')}` : ''
  return `${who}${x}: ${item.caption_snippet ?? 'пост'}`
}

function Badge({ item }: { item: FeedItem }) {
  if (item.metric_view === 'meta' && item.xn_status === 'warmup') return <span className="text-xs text-muted">Набираем базу</span>
  if (item.metric_view === 'meta' && item.xn_status === 'stale') return <span className="text-xs text-muted">{item.xn === null ? 'Нет свежей оценки' : `×${item.xn.toFixed(1).replace('.', ',')} · устарело`}</span>
  if (item.xn !== null) return <XBadge value={item.xn} />
  if (item.repost) {
    return (
      <span
        className="inline-flex h-5 items-center gap-1 rounded-md bg-x1-bg px-1.5 text-xs leading-none font-medium text-x1-fg"
        title="Репост: ×N не считается, оригинал не из наших источников"
      >
        <Repeat2 size={12} />
        репост
      </span>
    )
  }
  return null
}

function Marks({ item }: { item: FeedItem }) {
  const cls = 'inline-flex size-5 items-center justify-center rounded-md bg-x1-bg text-x1-fg'
  return (
    <>
      {item.is_early && (
        <span className={cls} title="Ранняя оценка — пост ещё набирает">
          <Hourglass size={12} aria-label="ранняя оценка" />
        </span>
      )}
      {item.low_data && (
        <span className={cls} title="Мало данных у автора — цифре можно верить меньше">
          <TriangleAlert size={12} aria-label="мало данных" />
        </span>
      )}
    </>
  )
}

/** У Reddit в тексте сначала заголовок, через пустую строку — сам пост. */
function RedditBody({ text }: { text: string | null }) {
  const [title, ...rest] = (text ?? '').split(/\n\s*\n/)
  const body = rest.join('\n\n').trim()
  return (
    <div className="min-w-0">
      <p className="line-clamp-3 text-[15px] leading-snug font-semibold text-pretty">{title || 'Без заголовка'}</p>
      {body && <p className="mt-1 line-clamp-3 text-[13px] leading-snug text-pretty text-muted">{body}</p>}
    </div>
  )
}

function InstagramBody({ item }: { item: FeedItem }) {
  const [broken, setBroken] = useState(false)
  const KindIcon = item.kind === 'carousel' ? Images : ImageIcon
  return (
    <div className="flex min-w-0 items-start gap-3">
      <div className="relative aspect-[4/5] w-16 shrink-0 overflow-hidden rounded-lg bg-surface ring-1 ring-line ring-inset desk:w-[72px]">
        {item.thumb_url && !broken ? (
          <img
            src={item.thumb_url}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setBroken(true)}
            className="absolute inset-0 size-full object-cover"
          />
        ) : (
          <KindIcon size={18} className="absolute top-1/2 left-1/2 -translate-1/2 text-muted" aria-hidden />
        )}
        <span
          className="absolute right-1 bottom-1 inline-flex size-[18px] items-center justify-center rounded bg-black/60 text-white"
          title={item.kind === 'carousel' ? 'Карусель' : 'Картинка'}
        >
          <KindIcon size={11} aria-label={item.kind === 'carousel' ? 'карусель' : 'картинка'} />
        </span>
      </div>
      <p className="line-clamp-5 min-w-0 flex-1 text-[14px] leading-snug text-pretty whitespace-pre-line">
        {item.caption_snippet ?? 'Без подписи'}
      </p>
    </div>
  )
}

function Stats({ item }: { item: FeedItem }) {
  const m = item.metrics
  if (item.platform === 'telegram') {
    return (
      <>
        {m.plays !== null && <Stat icon={<Eye size={13} />} value={formatCount(m.plays)} label="просмотры" />}
        {m.reactions !== null && <Stat icon={<Flame size={13} />} value={formatCount(m.reactions)} label="реакции" />}
        {m.comments !== null && <Stat icon={<MessageCircle size={13} />} value={formatCount(m.comments)} label="комментарии" />}
        {m.forwards !== null && <Stat icon={<Forward size={13} />} value={formatCount(m.forwards)} label="пересылки" />}
      </>
    )
  }
  if (item.platform === 'reddit') {
    return (
      <>
        {m.likes !== null && <Stat icon={<ArrowBigUp size={14} />} value={formatCount(m.likes)} label="очки" />}
        {m.comments !== null && <Stat icon={<MessageCircle size={13} />} value={formatCount(m.comments)} label="комментарии" />}
        {m.upvote_ratio !== null && <Stat icon={<ThumbsUp size={12} />} value={formatPct(m.upvote_ratio)} label="доля апвоутов" />}
      </>
    )
  }
  return (
    <>
      {m.likes !== null && <Stat icon={<Heart size={13} />} value={formatCount(m.likes)} label="лайки" />}
      {m.comments !== null && <Stat icon={<MessageCircle size={13} />} value={formatCount(m.comments)} label="комментарии" />}
    </>
  )
}

function Stat({ icon, value, label }: { icon: ReactNode; value: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1 tabular-nums" title={label}>
      {icon}
      {value}
    </span>
  )
}

export function TextCardSkeleton() {
  return (
    <div aria-hidden className="flex flex-col gap-2.5 rounded-xl bg-card p-3.5 ring-1 ring-line ring-inset">
      <div className="h-2.5 w-1/3 rounded bg-surface motion-safe:animate-pulse" />
      <div className="h-3 w-full rounded bg-surface motion-safe:animate-pulse" />
      <div className="h-3 w-11/12 rounded bg-surface motion-safe:animate-pulse" />
      <div className="h-3 w-2/3 rounded bg-surface motion-safe:animate-pulse" />
      <div className="mt-1 h-2.5 w-1/2 rounded bg-surface motion-safe:animate-pulse" />
    </div>
  )
}
